import type { MerchantResponse } from '@repo/api/endpoints/merchant';
import {
  areAllProviderNotPromoted,
  isPragmaPromoted,
  isLiberisPromoted,
  providerPreLimitValues,
  providerLimitValues,
} from '@repo/store/helpers';
import { providerType } from '@repo/shared/types';
import { useProviderStore } from '@repo/store/provider';
import { mockQueryResult } from '@repo/api/helpers';
import { useMainContext } from '@repo/app-contexts/main';
import { useGetMerchant } from '@repo/api/endpoints/merchant';

import { VIEW_TYPE } from '@/store/view.type';
import { useMainHubStoreActions } from '@/store/main-hub.store';
import { setCurrentViewForMerchantHashResponse } from '@/store/set-current-view-for-merchant-hash-response';

type HookResult = {
  shouldRenderButton: boolean;
  onClick: () => void;
};

export function useHandleMakeNewApplication(merchantResponseData?: MerchantResponse): HookResult {
  const { setAppState, setCurrentView } = useMainHubStoreActions();
  const {
    actions: { setCurrentProvider, setIsAvailableMultiprovider },
  } = useProviderStore();

  const { hash, token } = useMainContext();
  const { refetch } = useGetMerchant({ hash, token, enabled: false });

  if (!merchantResponseData || !merchantResponseData.currentProvider?.length) {
    return {
      shouldRenderButton: false,
      onClick: () => {},
    };
  }

  const isMultiprovider = merchantResponseData.currentProvider.length > 1;
  const notPromotedAnyProvider = areAllProviderNotPromoted(merchantResponseData);
  const pragmaPromoted = isPragmaPromoted(merchantResponseData);
  const liberisPromoted = isLiberisPromoted(merchantResponseData);

  const pragmaPreLimitValue = providerPreLimitValues(
    merchantResponseData,
    providerType.enum.pragma,
  );
  const pragmaLimitValue = providerLimitValues(merchantResponseData, providerType.enum.pragma);
  const liberisPreLimitValue = providerPreLimitValues(
    merchantResponseData,
    providerType.enum.liberis,
  );
  const liberisLimitValue = providerLimitValues(merchantResponseData, providerType.enum.liberis);

  const hasLiberisOffers = merchantResponseData.data.offers.length > 0;

  const pragmaLimit = pragmaLimitValue?.available ?? pragmaPreLimitValue?.max;
  const liberisLimit = liberisLimitValue?.available ?? liberisPreLimitValue?.max;

  const hasUserActivePragmaLoans = Boolean(
    merchantResponseData.data.loans.length > 0 &&
    merchantResponseData.data.loans.find(
      (loan) =>
        loan.stage === 5 && loan.provider === providerType.enum.pragma && loan.status === 'OPEN',
    ),
  );

  let onClick = () => {};

  if (isMultiprovider) {
    if (pragmaLimit && hasUserActivePragmaLoans) {
      onClick = () => {
        setCurrentView(VIEW_TYPE.LOAN_FORM_VIEW);
        setCurrentProvider(providerType.enum.pragma);
      };

      return { shouldRenderButton: true, onClick };
    }
    if (pragmaLimit && liberisLimit && notPromotedAnyProvider) {
      onClick = () => {
        const viewType = setCurrentViewForMerchantHashResponse({
          merchantResponseData: mockQueryResult(merchantResponseData),
          currentProvider: providerType.enum.liberis,
          omitDashboard: true,
        });
        setCurrentProvider(undefined);
        setCurrentView(viewType);
      };

      return { shouldRenderButton: true, onClick };
    }

    if (pragmaPromoted) {
      if (pragmaLimit) {
        onClick = () => {
          setCurrentView(VIEW_TYPE.LOAN_FORM_VIEW);
          setCurrentProvider(providerType.enum.pragma);
        };

        return { shouldRenderButton: true, onClick };
      }

      if (liberisLimit) {
        onClick = () => {
          setCurrentProvider(providerType.enum.liberis);
          const viewType = setCurrentViewForMerchantHashResponse({
            merchantResponseData: mockQueryResult(merchantResponseData),
            currentProvider: providerType.enum.liberis,
            omitDashboard: true,
          });
          setIsAvailableMultiprovider(false);
          setCurrentView(viewType);
        };

        return { shouldRenderButton: true, onClick };
      }

      return { shouldRenderButton: false, onClick };
    }

    if (liberisPromoted) {
      if (liberisLimit || hasLiberisOffers) {
        onClick = () => {
          setIsAvailableMultiprovider(false);
          const viewType = setCurrentViewForMerchantHashResponse({
            merchantResponseData: mockQueryResult(merchantResponseData),
            currentProvider: providerType.enum.liberis,
            omitDashboard: true,
          });
          setCurrentView(viewType);
        };

        return { shouldRenderButton: true, onClick };
      } else if (pragmaLimit) {
        onClick = () => {
          setCurrentView(VIEW_TYPE.LOAN_FORM_VIEW);
          setCurrentProvider(providerType.enum.pragma);
        };

        return { shouldRenderButton: true, onClick };
      }

      return { shouldRenderButton: false, onClick };
    }

    if (pragmaLimit && !liberisLimit && notPromotedAnyProvider) {
      onClick = () => {
        setCurrentView(VIEW_TYPE.LOAN_FORM_VIEW);
        setCurrentProvider(providerType.enum.pragma);
      };

      return { shouldRenderButton: true, onClick };
    }

    if (!pragmaLimit && liberisLimit && notPromotedAnyProvider) {
      onClick = () => {
        setCurrentProvider(providerType.enum.liberis);
        const viewType = setCurrentViewForMerchantHashResponse({
          merchantResponseData: mockQueryResult(merchantResponseData),
          currentProvider: providerType.enum.liberis,
          omitDashboard: true,
        });
        setIsAvailableMultiprovider(false);
        setCurrentView(viewType);
      };

      return { shouldRenderButton: true, onClick };
    }

    if (!pragmaLimit && !liberisLimit) {
      return { shouldRenderButton: false, onClick };
    }

    return { shouldRenderButton: false, onClick };
  }

  if (merchantResponseData.currentProvider[0] === providerType.enum.liberis) {
    onClick = () => {
      setCurrentView(VIEW_TYPE.APP_LOADING);
      void refetch().then((merchantQueryData) => {
        setCurrentProvider(providerType.enum.liberis);
        const viewType = setCurrentViewForMerchantHashResponse({
          merchantResponseData: merchantQueryData,
          currentProvider: providerType.enum.liberis,
          omitDashboard: true,
        });
        setAppState(viewType, merchantQueryData);
      });
    };

    return { shouldRenderButton: true, onClick };
  }

  onClick = () => {
    setCurrentView(VIEW_TYPE.APP_LOADING);
    void refetch().then((merchantQueryData) => {
      setCurrentProvider(providerType.enum.pragma);
      setAppState(VIEW_TYPE.LOAN_FORM_VIEW, merchantQueryData);
    });
  };

  return { shouldRenderButton: true, onClick };
}
